/**
 * Page design settings and the six legacy design themes (pages, dashboards and canvas v1).
 * New content uses `Theme` from `./theme`; `themeFromDesign` bridges the two.
 * Owner: doc-engine (wave 1), design-system (wave 2).
 */
import { z } from 'zod';
import type { DocumentNode } from './node';
import type { Theme, TextStyleKey } from './theme';

export const ThemeKey = z.enum(['studio', 'ocean', 'forest', 'terracotta', 'editorial', 'midnight']);
export type ThemeKey = z.infer<typeof ThemeKey>;
export const designThemes = {
  studio: {
    name: 'Studio',
    description: 'Clean & expressive',
    background: '#f7f8fc',
    surface: '#ffffff',
    ink: '#20263d',
    muted: '#64708b',
    accent: '#635bdb',
    soft: '#eeecfc',
    dark: '#252448',
    font: 'Arial',
  },
  ocean: {
    name: 'Ocean',
    description: 'Calm & confident',
    background: '#f3f8fb',
    surface: '#ffffff',
    ink: '#123147',
    muted: '#607b8c',
    accent: '#197fa2',
    soft: '#dfedf4',
    dark: '#10354b',
    font: 'Arial',
  },
  forest: {
    name: 'Forest',
    description: 'Natural & considered',
    background: '#f3f6ef',
    surface: '#ffffff',
    ink: '#203d32',
    muted: '#65796e',
    accent: '#377461',
    soft: '#e2eddc',
    dark: '#193e33',
    font: 'Arial',
  },
  terracotta: {
    name: 'Terracotta',
    description: 'Warm & distinctive',
    background: '#faf5ef',
    surface: '#fffcf8',
    ink: '#473329',
    muted: '#8b7265',
    accent: '#b65f44',
    soft: '#f3e3d7',
    dark: '#513329',
    font: 'Arial',
  },
  editorial: {
    name: 'Editorial',
    description: 'Timeless & precise',
    background: '#f5f3ee',
    surface: '#ffffff',
    ink: '#252827',
    muted: '#777a73',
    accent: '#77734b',
    soft: '#ebe8de',
    dark: '#282d2b',
    font: 'Georgia',
  },
  midnight: {
    name: 'Midnight',
    description: 'Bold & atmospheric',
    background: '#171c2c',
    surface: '#222a3f',
    ink: '#f1f3fb',
    muted: '#a5afc6',
    accent: '#b4a5ff',
    soft: '#333753',
    dark: '#101522',
    font: 'Arial',
  },
} as const;
export const imageSource = z
  .string()
  .max(2000)
  .refine(
    (v) =>
      !v ||
      /^https?:\/\/[^\s\\]+$/i.test(v) ||
      /^\/api\/v1\/(?:assets\/[a-f0-9-]{36}|public\/pages\/[a-f0-9]{64}\/assets\/[a-f0-9-]{36})$/.test(v),
    'Use an uploaded image or an HTTP image URL',
  );
export const DesignSettings = z
  .object({
    theme: ThemeKey.optional(),
    font: z.enum(['sans', 'serif', 'mono']).optional(),
    density: z.enum(['comfortable', 'compact']).optional(),
    radius: z.enum(['square', 'soft', 'rounded']).optional(),
    icon: z.string().max(16).optional(),
    cover: imageSource.optional(),
    coverHeight: z.number().int().min(140).max(400).optional(),
  })
  .strict();
export type DesignSettings = z.infer<typeof DesignSettings>;
export function designStyle(settings: DesignSettings = {}) {
  const t = designThemes[settings.theme ?? 'studio'];
  return {
    fontFamily: 'var(--design-font)',
    '--design-bg': t.background,
    '--design-surface': t.surface,
    '--design-ink': t.ink,
    '--design-muted': t.muted,
    '--design-accent': t.accent,
    '--design-soft': t.soft,
    '--design-radius': `${settings.radius === 'square' ? 0 : settings.radius === 'rounded' ? 24 : 12}px`,
    '--design-gap': settings.density === 'compact' ? '14px' : '24px',
    '--design-font':
      settings.font === 'serif'
        ? 'Georgia, serif'
        : settings.font === 'mono'
          ? '"Courier New", monospace'
          : settings.font === 'sans'
            ? 'Arial, sans-serif'
            : `${t.font}, sans-serif`,
  };
}
export const VisualElement = z
  .object({
    kind: z.enum(['metric', 'quote', 'steps', 'chart', 'progress', 'comparison']),
    title: z.string().max(160),
    description: z.string().max(600).optional(),
    value: z.string().max(80).optional(),
    label: z.string().max(100).optional(),
    items: z
      .array(
        z
          .object({
            label: z.string().max(80),
            detail: z.string().max(220).optional(),
            value: z.number().min(0).max(1e12).optional(),
          })
          .strict(),
      )
      .max(6)
      .optional(),
    progress: z.number().min(0).max(100).optional(),
    theme: ThemeKey.optional(),
    variant: z.enum(['soft', 'solid', 'outline']).optional(),
  })
  .strict();
export type VisualElement = z.infer<typeof VisualElement>;
export const visualPresets: Record<VisualElement['kind'], VisualElement> = {
  metric: {
    kind: 'metric',
    title: 'A number that matters',
    value: '84%',
    label: 'Add context for this metric',
    variant: 'soft',
  },
  quote: {
    kind: 'quote',
    title: 'A thought worth remembering.',
    description: 'Use a quote, a key insight, or your point of view.',
    label: 'Name · Role',
    variant: 'solid',
  },
  steps: {
    kind: 'steps',
    title: 'The path forward',
    items: [
      { label: 'Discover', detail: 'Understand the problem' },
      { label: 'Create', detail: 'Turn an idea into something useful' },
      { label: 'Deliver', detail: 'Measure what changes' },
    ],
  },
  chart: {
    kind: 'chart',
    title: 'Make the comparison clear',
    items: [
      { label: 'First', value: 42 },
      { label: 'Second', value: 68 },
      { label: 'Third', value: 89 },
    ],
  },
  progress: {
    kind: 'progress',
    title: 'Progress toward the goal',
    progress: 65,
    label: 'Describe what is complete and what comes next',
  },
  comparison: {
    kind: 'comparison',
    title: 'Explore the options',
    items: [
      { label: 'Option A', detail: 'Describe the benefits and tradeoffs.' },
      { label: 'Option B', detail: 'Describe the benefits and tradeoffs.' },
    ],
  },
};

/** Keep explicitly overridden components; update elements following the document theme. */
export function withDocumentDesign(doc: DocumentNode, design: DesignSettings): DocumentNode {
  const previous = (doc.attrs?.design as DesignSettings | undefined)?.theme ?? 'studio';
  const visit = (n: DocumentNode): DocumentNode => ({
    ...n,
    ...(n.type === 'visual' && (!n.attrs?.theme || n.attrs.theme === previous)
      ? { attrs: { ...n.attrs, theme: design.theme ?? 'studio' } }
      : {}),
    ...(n.content ? { content: n.content.map(visit) } : {}),
  });
  return { ...visit(doc), attrs: { ...doc.attrs, design } };
}

/**
 * The legacy design theme as a `Theme` (colour roles, fonts, text styles). Used to convert canvas v1
 * content and to render legacy themes with the new renderer; the colours are identical.
 */
export function themeFromDesign(key: ThemeKey): Theme {
  const t = designThemes[key];
  const font = t.font;
  const style = (size: number, weight: number, lineHeight: number, color: 'ink' | 'muted', extra = {}) => ({
    font: '@body' as const,
    size,
    weight,
    lineHeight,
    letterSpacing: 0,
    italic: false,
    transform: 'none' as const,
    color: { token: color },
    ...extra,
  });
  const textStyles: Record<TextStyleKey, Theme['textStyles'][TextStyleKey]> = {
    title: style(64, 700, 1.1, 'ink', { font: '@heading' }),
    heading: style(40, 700, 1.15, 'ink', { font: '@heading' }),
    subheading: style(28, 600, 1.25, 'ink', { font: '@heading' }),
    body: style(24, 400, 1.4, 'ink'),
    caption: style(16, 400, 1.35, 'muted'),
    label: style(14, 600, 1.3, 'muted', { letterSpacing: 0.06, transform: 'uppercase' }),
  };
  return {
    key: `legacy-${key}`,
    name: t.name,
    description: t.description,
    dark: key === 'midnight',
    colors: {
      background: t.background,
      surface: t.surface,
      ink: t.ink,
      muted: t.muted,
      accent: t.accent,
      soft: t.soft,
      dark: t.dark,
    },
    palette: [],
    fonts: { heading: font, body: font, mono: 'Courier New' },
    textStyles,
    radius: 12,
    chartPalette: [t.accent, t.dark, t.muted, t.soft],
  };
}
